import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree } from "@angular/forms/signals";
import { AmcAnalysisTemplate } from "@front/m19-api-client";
import { AuthService } from "@front/m19-services";
import { ICheckboxComponent, ILabelComponent } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";

export interface AmcAnalysisTemplateModel {
  templates: AmcAnalysisTemplate[];
  brandKeywords: string[];
}

@Component({
  selector: "app-amc-analysis-template-step",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, DatePipe, ICheckboxComponent, ILabelComponent],
  host: { class: "block" },
  template: `<div class="flex flex-col gap-6 py-8" *transloco="let t">
    <div class="flex flex-col gap-1">
      <h4>{{ t("common.template") }}</h4>
      <p class="text-muted-foreground m-0">{{ t("amc.strategy_analysis_template_description") }}</p>
    </div>
    <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
      @for (option of options(); track option) {
        <label iLabel type="card" class="relative w-full rounded-lg border p-4" [for]="option">
          <ICheckbox
            [inputId]="option"
            inputName="amcAnalysisTemplates"
            [value]="selected().has(option)"
            (valueChange)="toggle(option, $event)"
          />
          <div class="flex flex-col gap-2 pr-6">
            <div class="flex flex-col gap-1">
              <span class="type-body-medium">{{ t("amc-analysis-template." + option) }}</span>
              <p class="text-muted-foreground m-0">
                {{ t("amc-analysis-template." + option + "_DESCRIPTION") }}
              </p>
            </div>
            @if (firstIncompletePeriodEnds().get(option); as firstIncompleteEnd) {
              <span class="type-caption text-muted-foreground">
                {{
                  t("amc.strategy_analysis_template_incomplete", {
                    date: firstIncompleteEnd | date: "mediumDate" : undefined : locale(),
                  })
                }}
              </span>
            }
          </div>
        </label>
      }
    </div>
  </div>`,
})
export class AmcAnalysisTemplateStepComponent {
  private readonly authService = inject(AuthService);

  readonly template = input.required<FieldTree<AmcAnalysisTemplateModel>>();
  readonly options = input.required<AmcAnalysisTemplate[]>();
  readonly firstIncompletePeriodEnds = input<Map<AmcAnalysisTemplate, string>>(new Map());

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  readonly selected = computed(() => new Set(this.template().templates().value()));

  toggle(template: AmcAnalysisTemplate, checked: boolean) {
    const templates = this.template().templates().value();
    this.template()
      .templates()
      .value.set(checked ? [...templates, template] : templates.filter((t) => t !== template));
  }
}
